import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { darken } from "@mui/material/styles";
import { toLocalDatetimeString, MEDICINE_OPTIONS, getMedicineChipSx } from "./trackerHelpers";

const MedicineDialog = ({
  open,
  onClose,
  onSave,
  onDelete,
  initialStart,
  initialMedicines,
  initialFever,
}) => {
  const [datetime, setDatetime] = useState("");
  const [selectedMedicines, setSelectedMedicines] = useState([]);
  const [fever, setFever] = useState("");

  useEffect(() => {
    if (open) {
      setDatetime(
        initialStart
          ? toLocalDatetimeString(initialStart)
          : toLocalDatetimeString(new Date().toISOString())
      );
      setSelectedMedicines(initialMedicines ?? []);
      setFever(initialFever != null ? String(initialFever) : "");
    }
  }, [open, initialStart, initialMedicines, initialFever]);

  const handleSave = () => {
    const parsed = new Date(datetime);
    if (!datetime || isNaN(parsed.getTime())) return;
    const feverValue = fever !== "" ? parseFloat(fever) : null;
    onSave(parsed.toISOString(), selectedMedicines, feverValue);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" disableRestoreFocus>
      <DialogTitle>Registrer medicin</DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
      >
        <Box>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Tidspunkt
          </Typography>
          <TextField
            type="datetime-local"
            value={datetime}
            onChange={(e) => setDatetime(e.target.value)}
            fullWidth
            size="small"
          />
        </Box>
        <Box>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Feber (°C)
          </Typography>
          <TextField
            type="number"
            value={fever}
            onChange={(e) => setFever(e.target.value)}
            placeholder="38.5"
            inputProps={{ step: 0.1, min: 35, max: 43 }}
            size="small"
            sx={{ width: 120 }}
          />
        </Box>
        <Box>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Medicin
          </Typography>
          <ToggleButtonGroup
            value={selectedMedicines}
            onChange={(_, newValue) => setSelectedMedicines(newValue)}
            sx={{ flexWrap: "wrap", gap: 0.5 }}
          >
            {MEDICINE_OPTIONS.map((med) => {
              const chipSx = getMedicineChipSx(med);
              const hasColor = !!chipSx.backgroundColor;
              return (
                <ToggleButton
                  key={med}
                  value={med}
                  size="small"
                  sx={hasColor ? {
                    "&.Mui-selected": {
                      backgroundColor: chipSx.backgroundColor,
                      color: chipSx.color,
                      "&:hover": { backgroundColor: darken(chipSx.backgroundColor, 0.1) },
                    },
                  } : {}}
                >
                  {med}
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>
        </Box>
      </DialogContent>
      <DialogActions>
        {onDelete && (
          <Button color="error" onClick={onDelete} sx={{ mr: "auto" }}>
            Slet
          </Button>
        )}
        <Button onClick={onClose}>Annuller</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={(selectedMedicines.length === 0 && fever === "") || !datetime || isNaN(new Date(datetime).getTime())}
        >
          Gem
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MedicineDialog;
